import {useCallback, useEffect, useRef, useState} from 'react';
import dayjs from 'dayjs';
import {useLocation, useNavigate} from 'react-router-dom';
import {useMutation} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';
import {Controller, useForm} from 'react-hook-form';
import ChekinProSettings from 'ChekinProSettings';
import ChekinPro from 'ChekinPro';
import {useChekinSDK} from 'context/ChekinSDK';
import {
  FormTypes,
  OPTION_VALUES,
  SEARCH_RESERVATION_FORM_NAMES,
  formatOneDate,
  getMinCheckInDate,
  getNewToken,
  isAfter,
  isEqual,
} from 'pages/SearchReservation/utils';
import {AUTH_INIT_MODELS, DATE_FORMAT} from '@guestapp/sdk';
import {RESERVATION_ID_KEY, SHARE_LINK, STATUSES} from 'utils/constants';
import {PATHS} from 'Routes';
import {LocalStorage, PATTERNS, useStatus} from '@guestapp/core';
import {useErrorModal, useModalControls, usePath, useTimeoutRef} from 'hooks';
import {useHousing} from '../../useHousing';
import {useReservationNotFound} from '../../useReservationNotFound';
import PageUniversalLink from 'components/PageUniversalLink';
import Datepicker from 'components/common/Datepicker';
import {StatusModalStates} from 'components/StatusModal';
import {InputController as Input} from '@guestapp/ui';
import {createSession} from 'components/sessions/utils';
import {ReservationNotFoundModal} from 'components/ReservationNotFoundModal';
import {ViewMainInfo} from '../../components/ViewMainInfo';
import {SearchingBookingModal} from '../../components/SearchingBookingModal';
import {
  ContentButton,
  CustomBackButton,
  Fields,
  FormWrapper,
  StyledBackIcon,
  StyledButton,
  StyledFormFieldWrapper,
  Wrapper,
} from './styled';

function CheckInDateView() {
  const {t} = useTranslation();
  const {findReservation, modelName} = useChekinSDK();
  const navigate = useNavigate();
  const {state} = useLocation();
  const {getFullPath} = usePath();
  const {isLoading, setStatus} = useStatus();
  const {ErrorModal, displayError} = useErrorModal();
  const timeoutRef = useTimeoutRef();
  const {canCreate, isNotFoundModalOpen, openNotFoundModal, closeNotFoundModal} =
    useReservationNotFound();

  const payloadFormRef = useRef<
    Partial<
      Omit<FormTypes, SEARCH_RESERVATION_FORM_NAMES.check_in_date> & {
        [SEARCH_RESERVATION_FORM_NAMES.check_in_date]: string;
      }
    >
  >();
  const {housing, isLoading: isLoadingHousing, housingCountry} = useHousing();
  const isHousingLoading = modelName === AUTH_INIT_MODELS.housing && isLoadingHousing;

  const [modalRegisterGuestStatus, setModalRegisterGuestStatus] = useState(
    StatusModalStates.IDLE,
  );

  const {
    register,
    control,
    handleSubmit,
    trigger,
    formState: {errors, isSubmitted},
    watch,
  } = useForm<FormTypes>();
  const checkInDate = watch(SEARCH_RESERVATION_FORM_NAMES.check_in_date);
  const leadGuestEmail = watch(SEARCH_RESERVATION_FORM_NAMES.lead_email);

  const {
    isOpen: isOpenReservationStatusModal,
    openModal: openReservationStatusModal,
    closeModal: closeReservationStatusModal,
  } = useModalControls();

  const {mutate} = useMutation(
    (formData: FormTypes) => {
      const checkInDate = dayjs(
        formData[SEARCH_RESERVATION_FORM_NAMES.check_in_date],
      ).format(DATE_FORMAT);

      const payload = {
        [SEARCH_RESERVATION_FORM_NAMES.check_in_date]: checkInDate,
        [SEARCH_RESERVATION_FORM_NAMES.lead_email]:
          formData[SEARCH_RESERVATION_FORM_NAMES.lead_email],
      };
      return findReservation(payload);
    },
    {
      onSuccess: (data, formData) => {
        const reservationId = data?.results?.[0]?.id;
        const reservationSignupFormLink = data?.results?.[0]?.signup_form_link;

        if (reservationId) {
          if (ChekinProSettings.isSDK) {
            setStatus(STATUSES.success);
            setModalRegisterGuestStatus(StatusModalStates.SUCCESS);
            ChekinPro.events.onReservationFoundFromHousing({id: reservationId});
            return;
          }

          createSession({reservationId});

          LocalStorage.set(RESERVATION_ID_KEY, reservationId);
          LocalStorage.set(SHARE_LINK, reservationSignupFormLink);
          const tokenFromUrl = getNewToken(reservationSignupFormLink);
          window.location.href = `${import.meta.env.VITE_APP_PUBLIC_URL}/${tokenFromUrl}`;
          setModalRegisterGuestStatus(StatusModalStates.SUCCESS);
        } else {
          const checkInDate = dayjs(
            formData[SEARCH_RESERVATION_FORM_NAMES.check_in_date],
          ).format(DATE_FORMAT);

          const payload = {
            [SEARCH_RESERVATION_FORM_NAMES.check_in_date]: checkInDate,
            [SEARCH_RESERVATION_FORM_NAMES.lead_email]:
              formData[SEARCH_RESERVATION_FORM_NAMES.lead_email],
          };

          payloadFormRef.current = payload;
          setModalRegisterGuestStatus(StatusModalStates.IDLE);
          closeReservationStatusModal();
          setStatus(STATUSES.idle);
          canCreate ? goToNewReservation() : openNotFoundModal();
        }
      },
      onError: error => {
        setStatus(STATUSES.error);
        closeReservationStatusModal();
        displayError(error);
      },
    },
  );

  const onSubmit = (formData: FormTypes) => {
    setStatus(STATUSES.loading);
    openReservationStatusModal();
    setModalRegisterGuestStatus(StatusModalStates.LOADING);
    mutate(formData);
  };

  const validateCheckInDate = useCallback(
    (value?: Date) => {
      if (!value) return value;

      const minCheckInDate = getMinCheckInDate();
      const isAfterMinCheckInDate =
        isEqual(value, minCheckInDate) || isAfter(value, minCheckInDate);
      if (isAfterMinCheckInDate) return true;
      const formatMinCheckInDate = formatOneDate({
        date: minCheckInDate,
        housingCountry: housingCountry,
      });
      return t(`minimum_check_in_date_is`, {date: formatMinCheckInDate});
    },
    [housingCountry, t],
  );

  const goToNewReservation = () => {
    const payload = payloadFormRef.current;

    const checkInDate = payload?.[SEARCH_RESERVATION_FORM_NAMES.check_in_date];

    navigate(getFullPath(PATHS.searchReservation.newReservation), {
      replace: true,
      state: {
        startDate: checkInDate,
        email: payload?.[SEARCH_RESERVATION_FORM_NAMES.lead_email],
        housingId: housing?.id,
        country: housingCountry,
        from: OPTION_VALUES.check_in_date_and_email,
        mode: state?.mode,
      },
    });
  };

  useEffect(() => {
    if (
      !ChekinProSettings.isSDK &&
      modalRegisterGuestStatus === StatusModalStates.SUCCESS
    ) {
      timeoutRef.current = setTimeout(() => {
        closeReservationStatusModal();
      }, 3000);
    }

    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, [closeReservationStatusModal, modalRegisterGuestStatus, timeoutRef]);

  useEffect(
    function changeTranslationErrorMessages() {
      if (isSubmitted) {
        void trigger();
      }
    },
    [isSubmitted, trigger],
  );

  return (
    <PageUniversalLink className="check-in-date-view">
      <Wrapper>
        <ViewMainInfo
          title={t('find_your_booking')}
          subTitle={t('enter_the_check_in_date_and_the_lead_guest_email')}
        />

        {!state?.mode && (
          <CustomBackButton
            label={t('back')}
            customIcon={<StyledBackIcon name="backArrow" size={15} />}
            iconProps={{position: 'left'}}
            onClick={() =>
              navigate(getFullPath(PATHS.searchReservation.main), {
                state: {mode: OPTION_VALUES.check_in_date_and_email},
              })
            }
            width="auto"
            className="summary-view__back-btn"
            link
          />
        )}

        <FormWrapper>
          <Fields className="guest-form-fields">
            <StyledFormFieldWrapper>
              <Controller
                name={SEARCH_RESERVATION_FORM_NAMES.check_in_date}
                rules={{required: t('required'), validate: validateCheckInDate}}
                control={control}
                render={({field}) => {
                  return (
                    <Datepicker
                      error={errors[SEARCH_RESERVATION_FORM_NAMES.check_in_date]?.message}
                      label={t('check_in_date')}
                      {...field}
                    />
                  );
                }}
              />
            </StyledFormFieldWrapper>

            <StyledFormFieldWrapper>
              <Input
                {...register(SEARCH_RESERVATION_FORM_NAMES.lead_email, {
                  required: t('required'),
                  pattern: {
                    value: PATTERNS.email,
                    message: t('invalid_email'),
                  },
                })}
                type="email"
                label={t('lead_guest_email')}
                control={control}
                error={errors[SEARCH_RESERVATION_FORM_NAMES.lead_email]?.message}
                inputMode="email"
                autoCorrect="off"
                spellCheck={false}
                autoCapitalize="none"
              />
            </StyledFormFieldWrapper>
          </Fields>
        </FormWrapper>

        <ContentButton>
          <StyledButton
            label={t('confirm')}
            disabled={isLoading || !checkInDate || !leadGuestEmail || isHousingLoading}
            onClick={handleSubmit(onSubmit)}
            className="submit-btn"
          />
        </ContentButton>
      </Wrapper>
      <SearchingBookingModal
        isOpen={isOpenReservationStatusModal}
        status={modalRegisterGuestStatus}
      />
      <ErrorModal />
      <ReservationNotFoundModal open={isNotFoundModalOpen} onClose={closeNotFoundModal} />
    </PageUniversalLink>
  );
}
export {CheckInDateView};
